import { customElement } from 'lit/decorators.js';
import { TuftsHeader } from '../../tufts/tufts-header/tufts-header';
import { CSSResultGroup, html } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import '../outline-menu/outline-menu';
import tuftstHeaderStyle from '../../tufts/tufts-header/tufts-header.css.lit';
import componentStyles from './outline-header.css.lit';
/**
 * The Tufts Header Slot Component
 *
 * @element outline-header
 *
 * @slot translate
 *
 * @attr debug-menu
 * @attr menu-id
 * @attr menu-name
 * @attr menu-lang
 * @attr menu-type
 * @attr hamburger-menu-open
 * @attr hamburger-menu-name
 * @attr mobile-mode
 * @attr group-name-label
 * @attr group-menu-id
 * @attr no-group-label
 * @attr zipcode-label
 * @attr find-button-label
 * @attr current-language
 * @attr translate-label
 * @attr en-url
 * @attr es-url
 * @attr main-menu-label
 * @attr utility-menu-label
 * @attr account-menu-1-label
 * @attr account-menu-1-url
 * @attr account-menu-2-label
 * @attr account-menu-2-url
 * @attr donate-url
 * @attr donate-label
 * @attr en-label
 * @attr es-label
 * @attr search-url
 * @attr search-placeholder
 * @attr menu-label
 * @attr group-menu-label
 * @attr chapter-url
 * @attr warning-text
 */

@customElement('outline-header')
export class OutlineHeader extends TuftsHeader {
  static styles: CSSResultGroup = [tuftstHeaderStyle, componentStyles];

  _trapKeyboardMobileMenu(event: KeyboardEvent) {
    // Check if the pressed key is 'Tab'
    if (event.key === 'Tab') {
      // If 'Shift' key is also pressed, it means we are tabbing backwards
      if (event.shiftKey) {
        // Select all focusable elements in the main menu
        const focusableElements = document.querySelectorAll(
          'header .menu--main-menu .prototype-menu-link'
        );

        // If there are no focusable elements, return early
        if (focusableElements.length === 0) {
          return;
        }

        // Get the first focusable element in the main menu
        const firstElementMenu = focusableElements[0] as HTMLElement;

        // Check if the first focusable element in the main menu is the currently active element
        if (firstElementMenu === document.activeElement) {
          event.preventDefault();

          // Focus on the menu dropdown button in the 'outline-header' shadow DOM
          (
            document
              .querySelector('outline-header')
              ?.shadowRoot?.querySelector('button.menu-dropdown') as HTMLElement
          )?.focus();
        }
      } else {
        // If 'Shift' key is not pressed, we are tabbing forwards

        // Only trap the keyboard if the active element is the menu dropdown
        if (
          !document.activeElement?.shadowRoot?.activeElement?.matches(
            'button.menu-dropdown'
          )
        ) {
          return;
        }

        // Get all focusable elements in the main menu and the header utility
        const focusableElements = document.querySelectorAll(
          'header .menu--main-menu .prototype-menu-link,  header .menu--header-utility .prototype-menu-link'
        );

        // If there are no focusable elements, return early
        if (focusableElements.length === 0) {
          return;
        }
        event.preventDefault();

        // Focus on the first focusable element in the main menu or the header utility
        const firstFocusableElement = focusableElements[0] as HTMLElement;
        firstFocusableElement.focus();
      }
    }
  }

  mainMenuTemplate() {
    return html`
      <outline-menu
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="main-menu"
        depth="2"
        menu-name="${this.menuName}"
        menu-label="${this.mainMenuLabel}"
        menu-lang="${this.currentLanguage}"
        menu-heading-size="xs"
        mobile-trigger="true"
        color-mode="light"
        justify="right"
        top-menu-no-link
      >
        <slot name="main-menu"></slot>
      </outline-menu>
    `;
  }

  utilityMenuTemplate() {
    return html`
      <outline-menu
        json=${ifDefined(this.jsonHeaderUtilityMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="header-utility"
        menu-name="utility"
        menu-label="Utility"
        mobile-trigger="true"
        color-mode="dark"
        justify="right"
      >
        <slot name="header-utility"></slot>
      </outline-menu>
    `;
  }
  featureLinksMenuTemplate() {
    return html`
      <outline-menu
        json=${ifDefined(this.jsonFeatureLinksMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="feature-links"
        menu-name="feature-links"
        menu-label="Feature Links"
        menu-heading-size="xs"
        color-mode="dark"
      >
        <slot name="feature-links"></slot>
      </outline-menu>
    `;
  }

  headerMenuTemplate() {
    return html`
      <outline-menu
        json=${ifDefined(this.jsonHeaderMenuMenu)}
        ?mobile="${this.isMobile}"
        ?debug-menu="${this.debugMenu}"
        menu-id="header-menu"
        menu-name="header-menu"
        menu-label="Header menu"
        menu-heading-size="xs"
        color-mode="dark"
      >
        <slot name="header-menu"></slot>
      </outline-menu>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-header': OutlineHeader;
  }
}
